import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Boxes, Users as UsersIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import EmptyState from "../../components/system/EmptyState";
import ClickableStatCard from "../../components/system/ClickableStatCard";
import UserStatusBadge from "../../components/system/UserStatusBadge";
import {
  OperatorTable,
  OperatorTableRow,
} from "../../components/system/OperatorTable";

/**
 * OperatorDepartmentDetailPage — `/platform/companies/:cid/departments/:deptId`
 *
 * Per-department view:
 *   - Users in dept (read-only)
 *   - Drill-cards: active batches, removals 30d, pending PRs in dept
 *
 * Phase 1: READ-ONLY.
 */
export default function OperatorDepartmentDetailPage() {
  const { cid, deptId } = useParams();
  const [companyName, setCompanyName] = useState("");
  const [department, setDepartment] = useState(null);
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [batchesCount, setBatchesCount] = useState(0);
  const [removalsCount, setRemovalsCount] = useState(0);
  const [prsCount, setPrsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [det, dp, us, st, b, r, p] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/departments`),
        api.get(`/platform/companies/${cid}/users`),
        api.get(`/platform/companies/${cid}/stores`),
        api.get(
          `/platform/companies/${cid}/batches?status=active&department_id=${deptId}&limit=500`,
        ),
        api.get(
          `/platform/companies/${cid}/removals?days=30&department_id=${deptId}&limit=500`,
        ),
        api.get(
          `/platform/companies/${cid}/product-requests?status=pending&department_id=${deptId}&limit=500`,
        ),
      ]);
      setCompanyName(det.data.company?.name || "");
      const allDepts = dp.data.departments || [];
      const target = allDepts.find((d) => d.id === deptId);
      setDepartment(target || null);
      setUsers(
        (us.data.users || []).filter((u) => u.department_id === deptId),
      );
      setStores(st.data.stores || []);
      setBatchesCount(b.data.count || 0);
      setRemovalsCount(r.data.count || 0);
      setPrsCount(p.data.count || 0);
    } catch (e) {
      const msg = formatApiErrorDetail(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cid, deptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const storeName = useMemo(() => {
    const map = new Map(stores.map((s) => [s.id, s.name]));
    return (id) => (id ? map.get(id) || "—" : "—");
  }, [stores]);

  const activeCount = useMemo(
    () => users.filter((u) => u.active).length,
    [users],
  );

  if (loading) {
    return (
      <div data-testid="operator-department-detail-loading">
        <Card variant="raised" className="p-10 text-center">
          <div className="font-mono text-sm text-muted-foreground">
            Loading department…
          </div>
        </Card>
      </div>
    );
  }

  if (error || !department) {
    return (
      <div data-testid="operator-department-detail-error">
        <Link
          to={`/platform/companies/${cid}/departments`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink mb-4"
        >
          <ArrowLeft size={14} />
          Back to departments
        </Link>
        <EmptyState
          icon={<Boxes size={36} />}
          title="Department not found"
          description={error || "We could not load this department."}
        />
      </div>
    );
  }

  const GRID = "minmax(0,2fr) 1fr 1fr 1fr 1fr";

  return (
    <div data-testid="operator-department-detail-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}/departments`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="operator-department-detail-back"
        >
          <ArrowLeft size={14} />
          Back to departments
        </Link>
      </div>

      <PageHeader
        eyebrow={
          <span className="font-mono text-[11px]">
            Tenant · {companyName || cid} · id{" "}
            <span data-testid="operator-department-detail-id">
              {department.id}
            </span>
          </span>
        }
        title={department.name}
        subtitle="Read-only roll-up scoped to this department."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <ClickableStatCard
          label="Users"
          value={users.length}
          to={`/platform/companies/${cid}/users?department=${deptId}`}
          testid="operator-department-card-users"
          hint={`${activeCount} active`}
        />
        <ClickableStatCard
          label="Active batches"
          value={batchesCount}
          to={`/platform/companies/${cid}/batches?status=active&department=${deptId}`}
          testid="operator-department-card-batches"
        />
        <ClickableStatCard
          label="Removals (30d)"
          value={removalsCount}
          to={`/platform/companies/${cid}/removals?days=30&department=${deptId}`}
          testid="operator-department-card-removals"
        />
        <ClickableStatCard
          label="Open product requests"
          value={prsCount}
          to={`/platform/companies/${cid}/product-requests?status=pending&department=${deptId}`}
          testid="operator-department-card-prs"
        />
      </div>

      <section data-testid="operator-department-users-section">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h2 className="font-display font-black text-xl">
            Users in this department ({users.length})
          </h2>
          <Link
            to={`/platform/companies/${cid}/users?department=${deptId}`}
            className="font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
            data-testid="operator-department-users-view-all"
          >
            View all in Total users →
          </Link>
        </div>
        <OperatorTable
          gridTemplate={GRID}
          header={
            <>
              <div>Name</div>
              <div>Role</div>
              <div>Store</div>
              <div>Status</div>
              <div>Created</div>
            </>
          }
          empty={users.length === 0}
          emptyState={
            <div className="p-10">
              <EmptyState
                icon={<UsersIcon size={28} />}
                title="No users assigned to this department"
                description="Department assignment happens in the tenant admin UI."
                data-testid="operator-department-users-empty"
              />
            </div>
          }
          data-testid="operator-department-users-table"
        >
          {users.map((u) => (
            <OperatorTableRow
              key={u.id}
              gridTemplate={GRID}
              data-testid={`operator-department-user-row-${u.id}`}
            >
              <div className="min-w-0">
                <div className="font-display font-semibold truncate">
                  {u.name || u.email}
                </div>
                <div className="font-mono text-[11px] text-muted-foreground truncate">
                  {u.email}
                </div>
              </div>
              <div className="font-mono text-xs uppercase">{u.role}</div>
              <div className="font-mono text-xs truncate">
                {storeName(u.store_id)}
              </div>
              <div>
                <UserStatusBadge
                  active={!!u.active}
                  data-testid={`operator-department-user-status-${u.id}`}
                />
              </div>
              <div className="font-mono text-[11px] text-muted-foreground">
                {(u.created_at || "").slice(0, 10) || "—"}
              </div>
            </OperatorTableRow>
          ))}
        </OperatorTable>
      </section>
    </div>
  );
}
